import {
  ApportionedAmounts,
  ApportionmentContext,
  ApportionmentItem,
  isCostsItem,
  isQuantumItem,
} from "./apportionmentTypes.ts";

/**
 * Get the cap for an apportionment item from the context.
 */
function getCap(
  item: ApportionmentItem,
  { caps }: ApportionmentContext,
): number {
  if (isCostsItem(item)) {
    switch (item.costs) {
      case "ofSettlement":
        return caps.ofSettlement;
      case "towardsPayer":
        return caps.towardsPayer;
      case "generic":
        return caps.generic;
    }
  }
  if (isQuantumItem(item)) {
    return caps.quantums[item.quantumId] ?? 0;
  }
  return 0;
}

/**
 * Check if a quantum item is uncovered (no cap entry = not in settled claims).
 */
function isUncovered(
  item: ApportionmentItem,
  { caps }: ApportionmentContext,
): boolean {
  if (isQuantumItem(item)) {
    return !(item.quantumId in caps.quantums);
  }
  return false;
}

/**
 * Compute waterfall allocation of settlement funds across prioritized items.
 *
 * Items are processed in array order (position = priority).
 * Disabled and uncovered items are skipped (get 0).
 * - specificAmount undefined (max available): allocates min(remainingFunds, cap)
 * - specificAmount defined: allocates the specified amount (may exceed remaining)
 */
export function computeWaterfall(
  items: ApportionmentItem[],
  ctx: ApportionmentContext,
): ApportionedAmounts {
  let remainingFunds = ctx.settlementAmount;
  const result: ApportionedAmounts = {
    costs: {},
    quantums: {},
    total: 0,
    remaining: 0,
  };

  for (const item of items) {
    if (!item.enabled || isUncovered(item, ctx)) {
      continue;
    }

    const cap = getCap(item, ctx);
    let allocated: number;

    if (item.specificAmount !== undefined) {
      allocated = Math.min(item.specificAmount, Math.max(0, remainingFunds));
    } else {
      allocated = Math.min(remainingFunds, cap);
    }

    remainingFunds -= allocated;

    if (isCostsItem(item)) {
      switch (item.costs) {
        case "ofSettlement":
          result.costs.settlement = allocated;
          break;
        case "towardsPayer":
          result.costs.towardsPayer = allocated;
          break;
        case "generic":
          result.costs.generic = allocated;
          break;
      }
    } else if (isQuantumItem(item)) {
      result.quantums[item.quantumId] = allocated;
    }
  }

  result.total = ctx.settlementAmount - remainingFunds;
  result.remaining = remainingFunds;

  return result;
}

/**
 * Given an offer's claims and quantums, return only the quantums that are
 * covered by settled claims — deduplicated by title (highest value wins).
 *
 * This is the single source of truth for "which quantums does this offer cover?"
 * Used to build waterfall caps, sync apportionment items, and persist settledQuantums.
 */
export function getCoveredQuantums(
  claims: { settledByOffer: boolean; quantumIds: string[] }[],
  quantums: { id: string; title: string; value: number }[],
): { id: string; title: string; value: number }[] {
  const settledQuantumIds = new Set(
    claims.filter((c) => c.settledByOffer).flatMap((c) => c.quantumIds),
  );

  const coveredQuantums = quantums.filter((q) => settledQuantumIds.has(q.id));

  // Deduplicate by title — same quantum name can appear in multiple claims
  // with different IDs and values. Keep the highest value per title.
  const dedupedByTitle: Record<
    string,
    { id: string; title: string; value: number }
  > = {};
  for (const { id, title, value } of coveredQuantums) {
    const existing = dedupedByTitle[title];
    if (!existing || value > existing.value)
      dedupedByTitle[title] = { id, title, value };
  }

  return Object.values(dedupedByTitle);
}

/**
 * Ensure apportionment items are in sync with the offer's settled quantums.
 * Called on every load — handles all drift between apportionment and offer state:
 *   1. If no apportionment exists yet: generates all items (3 costs + quantums) in default state
 *   2. Quantum added to settledQuantums: new apportionment item appended (enabled, max-available)
 *   3. Quantum removed from settledQuantums: its apportionment item is discarded
 *   4. Existing items: preserved with their order, enabled state, and specificAmount
 *   5. Cost items are always present (never removed)
 */
export function ensureApportionmentItems(
  existing: ApportionmentItem[],
  coveredQuantumIds: string[],
): ApportionmentItem[] {
  // Deduplicate quantum IDs (same quantum may be referenced by multiple claims)
  const coveredSet = new Set(coveredQuantumIds);

  const defaultCosts: ApportionmentItem[] = [
    { costs: "ofSettlement", enabled: true },
    { costs: "towardsPayer", enabled: true },
    { costs: "generic", enabled: true },
  ];

  const result: ApportionmentItem[] = [];
  const seenCosts = new Set<string>();
  const seenQuantums = new Set<string>();

  // Preserve existing items in their current order
  for (const item of existing) {
    if (isCostsItem(item)) {
      result.push({ ...item });
      seenCosts.add(item.costs);
      continue;
    }
    // item must be quantum item
    if (coveredSet.has(item.quantumId)) {
      result.push({ ...item });
      seenQuantums.add(item.quantumId);
    }
  }

  // Add missing cost items at beginning
  const missingCosts: ApportionmentItem[] = [];
  for (const costItem of defaultCosts) {
    if (isCostsItem(costItem) && !seenCosts.has(costItem.costs)) {
      missingCosts.push(costItem);
    }
  }
  if (missingCosts.length > 0) result.unshift(...missingCosts);

  // Add new quantum items at end (iterate coveredSet to avoid duplicates)
  for (const quantumId of coveredSet) {
    if (!seenQuantums.has(quantumId)) result.push({ quantumId, enabled: true });
  }

  return result;
}
